/**
 * Step two of government-identity verification: the one-time code.
 *
 * Ported from the le-portal-ui-v3 prototype's `verification` step (src/LoginFlow.tsx),
 * with the part that matters moved server-side. This screen does not know the code, how
 * long it is, or how long the user must wait for another: the challenge it was handed
 * carries the length and the two instants, and the Web API decides whether a code is
 * right. A browser that could answer that question could be made to answer it wrongly
 * (PORT-DEFENSE).
 *
 * Both deadlines are absolute instants rather than durations, so the countdowns recompute
 * from the clock on every tick and survive a tab that was backgrounded or a machine that
 * slept, instead of drifting by however long it was away.
 */
import type { FormEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  Field,
  Input,
  MessageBar,
  mergeClasses,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  shorthands,
  Spinner,
  Text,
  Title2,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeftRegular, ShieldLockRegular } from '@fluentui/react-icons';
import type { ReceivedChallenge, VerificationResult } from '../../api/contracts';
import { FALLBACK_MESSAGE, isChallengeLost } from '../../api/errors';
import { isApiFailure, requestVerificationCode, verifyCode } from '../../api/verificationApi';
import {
  clockOffset,
  formatCountdown,
  maskEmail,
  normalizeEmail,
  resendOutcome,
  sanitizeCode,
  secondsUntil,
} from '../../govIdVerification';
import { useLoginStyles } from '../loginStyles';

const useStyles = makeStyles({
  codeField: {
    width: 'min(320px, 100%)',
    marginLeft: 'auto',
    marginRight: 'auto',
    '& input': {
      fontSize: tokens.fontSizeBase600,
      letterSpacing: '0.18em',
      textAlign: 'center',
    },
  },

  resendStatus: {
    display: 'grid',
    ...shorthands.gap(tokens.spacingVerticalS),
    justifyItems: 'center',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});

/**
 * What the last ask for a code did, which is all the banner above the field reports.
 *
 * `already-sent` is the server saying it was asked inside the cooldown and sent nothing.
 * It is a success, not an error: the challenge that came back is live, so the screen keeps
 * counting down and only changes what it tells the user to look for.
 */
type ResendState = 'idle' | 'sending' | 'sent' | 'already-sent';

export interface OtpPageProps {
  /**
   * The address the code was sent to. Shown back, masked, so the user can spot a typo, and
   * sent with every call: the server names a challenge by address alone today, so this is
   * the whole of what identifies the code being checked.
   */
  email: string;

  /** The challenge the server issued, which is what the typed code is checked against. */
  challenge: ReceivedChallenge;

  /**
   * Called with whatever challenge the server last named as live, whether or not that ask
   * sent a new code. An ask inside the cooldown still carries fresh deadlines, and those
   * are what the countdowns here run to.
   */
  onChallengeChange: (challenge: ReceivedChallenge) => void;

  /** Called once the server accepts the code, with what it said is still to register. */
  onVerified: (result: VerificationResult) => void;

  /** Returns to the address step, dropping the challenge. */
  onBack: () => void;
}

export function OtpPage({
  email,
  challenge,
  onChallengeChange,
  onVerified,
  onBack,
}: OtpPageProps) {
  const loginStyles = useLoginStyles();
  const styles = useStyles();
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Set when the server says the code is past its life. Typing is pointless from that
  // moment — there is nothing left to check against — so the screen stops offering it and
  // offers the only thing that can help instead. A wrong code is not this: it leaves the
  // challenge live and the user free to try again.
  const [challengeLost, setChallengeLost] = useState(false);

  // One ticking value drives both countdowns; the deadlines themselves never tick.
  const [now, setNow] = useState(() => Date.now());

  const codeInputRef = useRef<HTMLInputElement>(null);

  // The deadlines in a challenge were chosen by the server's clock, so they are only
  // countdowns if read against the server's clock. Both halves of the measurement come off
  // the challenge, so this is pure: React may drop the memo and recompute it without the
  // answer changing, which reading the clock here would not have survived.
  const offsetMs = useMemo(
    () => clockOffset(challenge.serverTime, challenge.receivedAt),
    [challenge],
  );

  const serverNow = now + offsetMs;
  const resendSeconds = secondsUntil(challenge.resendAvailableAt, serverNow);
  const expirySeconds = secondsUntil(challenge.expiresAt, serverNow);
  const isCountingDown = resendSeconds > 0 || expirySeconds > 0;

  useEffect(() => {
    if (!isCountingDown) {
      return;
    }

    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isCountingDown]);

  // A new challenge means new deadlines; recompute against the clock immediately rather
  // than showing the previous challenge's countdown until the next tick.
  useEffect(() => {
    setNow(Date.now());
  }, [challenge]);

  const handleFailure = (error: unknown) => {
    if (isApiFailure(error)) {
      setErrorMessage(error.message);
      setChallengeLost(isChallengeLost(error.code));
      return;
    }

    setErrorMessage(FALLBACK_MESSAGE);
  };

  const submitCode = async () => {
    setIsVerifying(true);
    setErrorMessage(null);
    setResendState('idle');

    try {
      onVerified(await verifyCode(normalizeEmail(email), code));
    } catch (error) {
      handleFailure(error);
    } finally {
      setIsVerifying(false);
    }
  };

  const requestNewCode = async () => {
    setResendState('sending');
    setErrorMessage(null);

    try {
      const refreshed = await requestVerificationCode(normalizeEmail(email));
      const outcome = resendOutcome(refreshed.codeSent);

      if (!outcome.keepTypedCode) {
        setCode('');
      }

      setResendState(outcome.banner);
      onChallengeChange(refreshed);
    } catch (error) {
      setResendState('idle');
      handleFailure(error);
    } finally {
      // The Resend button is replaced by the countdown the moment this settles, so a
      // keyboard user who pressed it has nothing left under focus. Typing the code is what
      // they do next either way, so focus goes to the field. A no-op if the challenge was
      // lost, which disables it.
      codeInputRef.current?.focus();
    }
  };

  const maskedEmail = maskEmail(email) || email;
  const isBusy = isVerifying || resendState === 'sending';
  const canSubmit = code.length === challenge.codeLength && !isBusy && !challengeLost;

  // Enter and the button are the same action, so both go through this. When the challenge
  // is lost there is no submit button at all, and Enter correctly does nothing.
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    void submitCode();
  };

  return (
    <Card appearance="outline" className={mergeClasses(loginStyles.card, loginStyles.cardNarrow)}>
      <div className={loginStyles.stepHeading}>
        <div className={loginStyles.centeredHeading}>
          <span className={mergeClasses(loginStyles.headingIcon, loginStyles.centeredHeadingIcon)} aria-hidden="true">
            <ShieldLockRegular />
          </span>
          <Title2 as="h1" id="login-content-title" tabIndex={-1}>
            Enter your verification code
          </Title2>
          <p>
            We sent a {challenge.codeLength}-digit code to your official agency email:{' '}
            <strong>{maskedEmail}</strong>.
          </p>
        </div>
      </div>

      <Divider />

      {resendState === 'sent' && errorMessage === null && (
        <MessageBar intent="success" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>New code sent</MessageBarTitle>
            Check your official agency email for the new code.
          </MessageBarBody>
        </MessageBar>
      )}

      {resendState === 'already-sent' && errorMessage === null && (
        <MessageBar intent="info" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Your code is still on its way</MessageBarTitle>
            We already sent a code to this address. Check your official agency email, and use
            the countdown below if you still need a new one.
          </MessageBarBody>
        </MessageBar>
      )}

      {errorMessage !== null && (
        <MessageBar intent={challengeLost ? 'warning' : 'error'} layout="multiline">
          <MessageBarBody>{errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      <form className={loginStyles.stepForm} onSubmit={handleSubmit}>
        <Field
          className={styles.codeField}
          label="Verification code"
          hint={
            challengeLost
              ? undefined
              : expirySeconds > 0
                ? `This code expires in ${formatCountdown(expirySeconds)}.`
                : 'This code has expired. Request a new one to continue.'
          }
        >
          <Input
            ref={codeInputRef}
            aria-invalid={errorMessage !== null}
            // readOnly rather than disabled while the request is in flight. Enter submits
            // from this field, so a keyboard user is standing in it when the call starts;
            // disabling blurs them, and when the call settles on a wrong code they would
            // have to find their way back in before they could retype. readOnly refuses
            // the keystrokes without taking the focus, and canSubmit already blocks a
            // second submit. Still disabled once the challenge is lost: there is nothing
            // left to type, and the screen offers a new code instead.
            disabled={challengeLost}
            readOnly={isBusy}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            placeholder={'0'.repeat(challenge.codeLength)}
            onChange={(_, data) => {
              setCode(sanitizeCode(data.value, challenge.codeLength));
              // Editing is the user answering the error, so the error and the stale
              // "new code sent" banner both go with the keystroke.
              setErrorMessage(null);
              setResendState('idle');
            }}
          />
        </Field>

        {!challengeLost && (
          <div className={styles.resendStatus}>
            <Text>Didn't receive the code?</Text>
            {resendSeconds > 0 ? (
              <Text size={200}>
                You can request a new code in {formatCountdown(resendSeconds)}
              </Text>
            ) : (
              <Button
                appearance="subtle"
                type="button"
                disabled={isBusy}
                icon={resendState === 'sending' ? <Spinner size="tiny" /> : undefined}
                onClick={() => {
                  void requestNewCode();
                }}
              >
                {resendState === 'sending' ? 'Sending code' : 'Resend code'}
              </Button>
            )}
          </div>
        )}

        <div className={loginStyles.footerActions}>
          <Button
            appearance="secondary"
            type="button"
            icon={<ArrowLeftRegular />}
            disabled={isBusy}
            onClick={onBack}
          >
            Back
          </Button>
          {challengeLost ? (
            // Nothing typed here can be accepted any more, so the only action offered is the
            // one that can actually produce a usable code.
            <Button appearance="primary" type="button" onClick={onBack}>
              Request a new code
            </Button>
          ) : (
            <Button
              appearance="primary"
              type="submit"
              disabled={!canSubmit}
              icon={isVerifying ? <Spinner size="tiny" /> : undefined}
            >
              {isVerifying ? 'Verifying email' : 'Verify email'}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}
